import { useEffect, useState } from "react"
import mixpanel from "mixpanel-browser"
import { omitBy, isUndefined } from "lodash"
import { warn } from "helpers/dev-logger"

const DEFAULT_HOST = "https://api-js.mixpanel.com"
const IS_PRODUCTION = process.env.NODE_ENV === "production"

// Module-level: persists across React unmount/remount so remounts skip re-init.
let isMixpanelReady = false

// [TODO:TS] Placeholder type for event property values, will be replaced with the actual event property value type and properties once determined.
type EventPropertyValue = string | number | boolean | null | undefined

export interface UseMixPanelOptions {
  distinctId: string
  eventProperties?: Record<string, EventPropertyValue>
  sessionSampleRate?: number
}

function readMixpanelToken(): string | null {
  const bundle = document.getElementById("app")?.getAttribute("data-bundle")
  if (!bundle) {
    return null
  }

  try {
    const data = JSON.parse(bundle) as {
      mixpanelToken?: string
    }

    return data.mixpanelToken ?? null
  } catch {
    return null
  }
}

export default function useMixPanel(options: UseMixPanelOptions): void {
  const {
    distinctId = null,
    eventProperties,
    sessionSampleRate = 100,
  } = options

  // Seed from module-level flag so a remount starts ready when the singleton already initialized.
  const [mpReady, setMpReady] = useState(isMixpanelReady)

  useEffect(() => {
    if (!IS_PRODUCTION || !distinctId || mpReady) {
      return
    }

    const token = readMixpanelToken()
    if (!token) {
      return
    }

    try {
      // Security: consult the team before changing session recording params — masking prevents sensitive data leaks.
      mixpanel.init(token, {
        api_host: DEFAULT_HOST,
        record_sessions_percent: sessionSampleRate,
        autocapture: {
          pageview: "full-url",
          click: true,
          input: true,
          scroll: true,
          submit: true,
          capture_text_content: false,
        },
        // __loaded is not set synchronously — use loaded callback as the readiness gate.
        loaded: () => {
          isMixpanelReady = true
          setMpReady(true)
        },
      })
    } catch (err) {
      warn("[Mixpanel] init failed", err)
    }
  }, [sessionSampleRate, distinctId, mpReady])

  useEffect(() => {
    if (!IS_PRODUCTION || !mpReady || !distinctId) {
      return
    }

    try {
      mixpanel.register(omitBy(eventProperties ?? {}, isUndefined))
    } catch (err) {
      warn("[Mixpanel] register failed", err)
    }
  }, [eventProperties, distinctId, mpReady])

  useEffect(() => {
    if (!IS_PRODUCTION || !mpReady || !distinctId) {
      return
    }

    try {
      mixpanel.identify(distinctId)
    } catch (err) {
      warn("[Mixpanel] identify failed", err)
    }
  }, [distinctId, mpReady])

  useEffect(() => {
    if (!IS_PRODUCTION || !mpReady || !distinctId) {
      return
    }

    try {
      mixpanel.opt_in_tracking()
      mixpanel.start_session_recording()
    } catch (err) {
      warn("[Mixpanel] start_session_recording failed", err)
    }

    return () => {
      try {
        mixpanel.stop_session_recording()
        mixpanel.opt_out_tracking()
      } catch (err) {
        warn("[Mixpanel] stop_session_recording failed", err)
      }
    }
  }, [distinctId, mpReady])
}
